import React from 'react';
import { Box, Paper, Typography, Chip, Checkbox, Button, Stack, Tooltip } from '@mui/material';
import {
  Bolt as ApiIcon, PhoneIphone as AppIcon, ChevronRight as OpenIcon, ContentCopy as CopyIcon,
  WarningAmber as WarnIcon, HourglassTop as WaitIcon,
} from '@mui/icons-material';
import { PanelItem, brl, dataBR } from '@/services/aprovador';

export const DUE: Record<string, { label: string; color: 'error' | 'warning' | 'default' | 'info' }> = {
  vencido: { label: 'Vencido', color: 'error' },
  hoje: { label: 'Vence hoje', color: 'warning' },
  a_vencer: { label: 'A vencer', color: 'default' },
  sem_vencimento: { label: 'Sem vencimento', color: 'default' },
};

export const FORMA: Record<string, string> = {
  pix_chave: 'PIX por chave',
  pix_qr_estatico: 'PIX QR estático',
  pix_qr_dinamico: 'PIX QR dinâmico',
  pix_qr: 'PIX copia e cola',
  boleto: 'Boleto',
  arrecadacao: 'Guia / conta de consumo',
};

interface Props {
  item: PanelItem;
  selected: boolean;
  onToggle: () => void;
  onOpen: () => void;
}

const ItemCard: React.FC<Props> = ({ item, selected, onToggle, onOpen }) => {
  const due = DUE[item.dueStatus];
  const processing = item.status === 'processing';
  const borda = item.dueStatus === 'vencido' ? '#d32f2f' : item.dueStatus === 'hoje' ? '#ed6c02' : '#cfd8e3';

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, borderLeft: `4px solid ${borda}`, bgcolor: selected ? '#f0f6ff' : '#fff', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', gap: 1, p: 1.5, pb: 1 }}>
        <Checkbox checked={selected} onChange={onToggle} disabled={processing} sx={{ p: 0.5, alignSelf: 'flex-start' }}
          inputProps={{ 'aria-label': 'Selecionar' }} />
        <Box sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={onOpen} role="button">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
            <Typography variant="h6" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>{brl(item.amount)}</Typography>
            <Typography variant="body2" fontWeight={600} color={item.dueStatus === 'vencido' ? 'error.main' : item.dueStatus === 'hoje' ? 'warning.dark' : 'text.secondary'}>
              {item.dueDate ? dataBR(item.dueDate) : '—'}
            </Typography>
          </Box>
          <Typography variant="body1" fontWeight={600} sx={{ lineHeight: 1.3, wordBreak: 'break-word' }}>
            {item.beneficiaryName || item.pixKey || 'Favorecido não informado'}
          </Typography>
          {item.description && (
            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>{item.description}</Typography>
          )}
          <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.75 }}>
            <Chip size="small" label={due.label} color={due.color} variant={due.color === 'default' ? 'outlined' : 'filled'} />
            {item.payVia === 'api'
              ? <Chip size="small" icon={<ApiIcon />} label="Paga pela API" sx={{ bgcolor: '#e6f4ea', color: '#1e6b34', '& .MuiChip-icon': { color: '#1e6b34' } }} />
              : <Chip size="small" icon={<AppIcon />} label="Pagar no app" sx={{ bgcolor: '#fff4e5', color: '#8a4b00', '& .MuiChip-icon': { color: '#8a4b00' } }} />}
            {item.bank === 'efi' && <Chip size="small" variant="outlined" label="Sai da conta Efí" />}
            <Chip size="small" variant="outlined" label={FORMA[item.subtype] || item.kindLabel} />
            {processing && <Chip size="small" color="info" icon={<WaitIcon />} label="Processando" />}
            {item.awaitingConfirmation && !processing && <Chip size="small" color="info" variant="outlined" label="Já conferido" />}
            {item.duplicateCount && (
              <Tooltip title="O mesmo título aparece mais de uma vez na lista"><Chip size="small" color="secondary" icon={<CopyIcon />} label={`Repetido ${item.duplicateCount}×`} /></Tooltip>
            )}
          </Stack>
          {item.payVia === 'app' && item.appReason && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>{item.appReason}</Typography>
          )}
          {item.guiaVencida && (
            <Typography variant="caption" color="error.main" display="flex" alignItems="center" gap={0.5} sx={{ mt: 0.5 }}>
              <WarnIcon sx={{ fontSize: 14 }} /> Guia vencida não quita nem no app: precisa ser reemitida.
            </Typography>
          )}
        </Box>
      </Box>



      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderTop: '1px solid #eef1f5', bgcolor: '#fafbfc', flexWrap: 'wrap' }}>
        <Button size="small" onClick={onOpen} endIcon={<OpenIcon />} sx={{ color: 'text.secondary' }}>
          Detalhes
        </Button>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {processing ? 'Em processamento' : item.payVia === 'api' ? 'Toque para conferir e aprovar' : 'Toque para pagar ou dar baixa'}
        </Typography>
      </Box>
    </Paper>
  );
};

export default ItemCard;
